import { AlertCircle, Check, Database, Laptop, Loader2 } from 'lucide-react'
import {
  AiChatEvent,
  QueryUsecaseTableResult,
  ShowUsecaseTablesResult,
  SearchSecuritiesReportChunkResult,
  QueryRawSqlUsecaseTableResult,
} from 'poc-ai-biz-irdb_domain-model'
import React, { useState } from 'react'
import { cn } from '../../../../../_shared/lib'
import {
  AiChatEventPayloadUserMessage,
  AiChatEventPayloadServerSideToolCall,
  AiChatEventPayloadClientInfo,
  AiChatEventPayloadClientAction,
  AiChatEventPayloadAssistantMessage,
  AiChatEventPayloadError,
} from '../../model'
import { SourceDocumentPane } from './SourceDocumentPane'
import { MarkdownRenderer } from '../../../../molecules/MarkdownRenderer'
import { Badge } from '../../../../molecules/badge'

const AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH = '768px'
const CHAT_WIDTH = 768 // px

const AiChatEventPayloadUserMessageComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadUserMessage
  }
}) => (
  <div className={`w-[${AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH}] flex flex-col items-end gap-1`}>
    <Badge className="bg-blue-600 text-white px-3 py-2 h-auto max-w-full whitespace-normal break-words text-sm leading-relaxed" variant="default">
      {aiChatEvent.payload.content}
    </Badge>
    <span className="text-xs text-gray-400">{aiChatEvent.createdAt.toLocaleTimeString()}</span>
  </div>
)

const AiChatEventPayloadAssistantMessageComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadAssistantMessage
  }
}) => (
  <div className={`w-[${AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH}] flex flex-col items-start`}>
    <MarkdownRenderer markdown={aiChatEvent.payload.content ?? ''} />
    <span className="text-xs text-gray-400">{aiChatEvent.createdAt.toLocaleTimeString()}</span>
  </div>
)

const AiChatEventPayloadServerSideToolCallComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadServerSideToolCall
  }
}) => {
  const { payloadJsonStatus, payload } = aiChatEvent

  return (
    <div className={`w-[${AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH}] flex flex-row pl-2 items-center gap-2`}>
      {payloadJsonStatus === 'generating' ? (
        <Loader2 className="h-3 w-3 text-gray-500 animate-spin" />
      ) : payload.status === 'executing' ? (
        <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
      ) : payload.status === 'completed' ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <AlertCircle className="h-3 w-3 text-red-600" />
      )}
      <Database className="h-3 w-3 text-gray-500" />
      <div className="flex flex-col gap-0 flex-1 items-start">
        <span className="text-xs text-gray-500 leading-snug">{payload.displayTextForUser}</span>
        <div className="flex flex-row gap-1 items-center">
          {Object.entries(payload.parameters ?? {}).map(([key, value]) => (
            <span key={key} className="text-[8px] text-gray-500">
              {Array.isArray(value) ? value.join(', ') : String(value)}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const AiChatEventPayloadClientInfoComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadClientInfo
  }
}) => (
  <div className={`w-[${AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH}] flex flex-row pl-2 items-center gap-2`}>
    <Check className="h-3 w-3 text-green-600" />
    <Laptop className="h-3 w-3 text-gray-500" />
    <span className="text-xs text-gray-500 leading-snug">ページ内容を送信しました</span>
  </div>
)

const AiChatEventPayloadClientActionComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadClientAction
  }
}) => {
  const { payloadJsonStatus, payload } = aiChatEvent
  return (
    <div className={`w-[${AI_CHAT_EVENT_PAYLOAD_COMPONENT_BASIS_WIDTH}] flex flex-row pl-2 items-center gap-2`}>
      {payloadJsonStatus === 'generating' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 text-green-600" />}
      <Laptop className="h-3 w-3 text-gray-500" />
      <span className="text-xs text-gray-500 leading-snug">{payload.displayTextForUser}</span>
    </div>
  )
}

const AiChatEventPayloadErrorComponent = ({
  aiChatEvent,
}: {
  aiChatEvent: AiChatEvent & {
    payload: AiChatEventPayloadError
  }
}) => <span className="text-sm text-red-600">{aiChatEvent.payload.message}</span>

// aiChatEventsをユーザーメッセージごとにパーティション分割する関数
export const partitionAiChatEventsByUserMessage = (aiChatEvents: AiChatEvent[], isSendingMessage: boolean): AiChatEventsPartitionedByUserMessageProps[] => {
  const partitions: Omit<AiChatEventsPartitionedByUserMessageProps, 'isLast' | 'isSendingMessage'>[] = []
  let currentPartition: Partial<Omit<AiChatEventsPartitionedByUserMessageProps, 'isLast' | 'isSendingMessage'>> | null = null

  for (const event of aiChatEvents) {
    const payload = event.payload

    if (payload.type === 'userMessage') {
      // 新しいパーティションを開始
      if (currentPartition && currentPartition.userMessageEvent) {
        partitions.push(currentPartition as Omit<AiChatEventsPartitionedByUserMessageProps, 'isLast' | 'isSendingMessage'>)
      }
      currentPartition = {
        userMessageEvent: { ...event, payload },
        toolEvents: [],
        assistantMessageEvents: [],
      }
    } else if (currentPartition) {
      // 現在のパーティションにイベントを追加
      if (payload.type === 'serverSideToolCall' || payload.type === 'clientInfo' || payload.type === 'clientAction') {
        currentPartition.toolEvents!.push({ ...event, payload })
      } else if (payload.type === 'assistantMessage' || payload.type === 'error') {
        currentPartition.assistantMessageEvents!.push({ ...event, payload })
      }
    }
  }

  // 最後のパーティションを追加
  if (currentPartition && currentPartition.userMessageEvent) {
    partitions.push(currentPartition as Omit<AiChatEventsPartitionedByUserMessageProps, 'isLast' | 'isSendingMessage'>)
  }

  // isLastとisSendingMessageを追加
  return partitions.map((partition, index) => ({
    ...partition,
    isLast: index === partitions.length - 1,
    isSendingMessage,
  }))
}

export type AiChatEventsPartitionedByUserMessageProps = {
  userMessageEvent: AiChatEvent & { payload: AiChatEventPayloadUserMessage }
  toolEvents: (AiChatEvent & { payload: AiChatEventPayloadServerSideToolCall | AiChatEventPayloadClientInfo | AiChatEventPayloadClientAction })[]
  assistantMessageEvents: (AiChatEvent & { payload: AiChatEventPayloadAssistantMessage | AiChatEventPayloadError })[]
  isLast: boolean
  isSendingMessage: boolean
  isAnySourceDocumentExpanded: boolean
  onSourceDocumentExpandedChange: (isExpanded: boolean) => void
}
export const AiChatEventsPartitionedByUserMessage = (props: AiChatEventsPartitionedByUserMessageProps) => {
  const { userMessageEvent, toolEvents, assistantMessageEvents, isLast, isSendingMessage, isAnySourceDocumentExpanded, onSourceDocumentExpandedChange } = props
  const [isToolEventsExpanded, setIsToolEventsExpanded] = useState(false)

  // Expanded state management
  const [expandedQueryUsecaseTableResults, setExpandedQueryUsecaseTableResults] = useState<Map<string, QueryUsecaseTableResult>>(new Map())
  const [expandedShowUsecaseTablesResults, setExpandedShowUsecaseTablesResults] = useState<Map<string, ShowUsecaseTablesResult>>(new Map())

  // 一つでも展開されているかの判定
  const isExpanded = expandedQueryUsecaseTableResults.size > 0 || expandedShowUsecaseTablesResults.size > 0

  // isExpandedが変更されたら親に通知
  React.useEffect(() => {
    onSourceDocumentExpandedChange(isExpanded)
  }, [isExpanded, onSourceDocumentExpandedChange])

  // Click handlers
  const handleQueryUsecaseTableClick = (key: string, data: QueryUsecaseTableResult) => {
    setExpandedQueryUsecaseTableResults((prev) => {
      const newMap = new Map(prev)
      if (newMap.has(key)) {
        newMap.delete(key)
      } else {
        newMap.clear() // 一つだけ展開する場合
        newMap.set(key, data)
      }
      return newMap
    })
  }

  const handleShowUsecaseTableClick = (key: string, data: ShowUsecaseTablesResult) => {
    setExpandedShowUsecaseTablesResults((prev) => {
      const newMap = new Map(prev)
      if (newMap.has(key)) {
        newMap.delete(key)
      } else {
        newMap.clear() // 一つだけ展開する場合
        newMap.set(key, data)
      }
      return newMap
    })
  }

  const handleCloseDetail = () => {
    setExpandedQueryUsecaseTableResults(new Map())
    setExpandedShowUsecaseTablesResults(new Map())
  }

  // このパーティションのtoolEventsからSourceDocumentsを抽出
  const queryUsecaseTableResults: QueryUsecaseTableResult[] = []
  const securitiesReportChunkResults: SearchSecuritiesReportChunkResult[] = []
  const showUsecaseTablesResults: ShowUsecaseTablesResult[] = []
  const queryRawSqlUsecaseTableResults: QueryRawSqlUsecaseTableResult[] = []

  for (const event of toolEvents) {
    if (event.payload.type === 'serverSideToolCall' && event.payload.result) {
      const result = event.payload.result
      if (!result || !('toolType' in result)) {
        continue
      }
      if (result.toolType === 'query_usecase_table') {
        queryUsecaseTableResults.push(result)
      } else if (result.toolType === 'search_securities_reports_chunk') {
        securitiesReportChunkResults.push(result)
      } else if (result.toolType === 'show_usecase_tables') {
        showUsecaseTablesResults.push(result)
      } else if (result.toolType === 'query_raw_sql_usecase_table') {
        queryRawSqlUsecaseTableResults.push(result)
      }
    }
  }

  // Securities Report Chunk クリックハンドラー
  const handleSecuritiesReportChunkClick = (data: SearchSecuritiesReportChunkResult, index: number) => {
    // 新しいタブで開く
    window.open(`/securities-report-chunk/${index}`, '_blank', 'noopener,noreferrer')
  }

  // Query Raw SQL Usecase Table クリックハンドラー
  const handleQueryRawSqlUsecaseTableClick = (url: string) => {
    // 新しいタブで開く
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // 最初のassistantMessageとそれ以降のassistantMessageを分離
  const firstAssistantMessage = assistantMessageEvents.length > 0 ? assistantMessageEvents[0] : null
  const restAssistantMessages = assistantMessageEvents.slice(1)

  // チャットエリアのコンテンツ
  const chatContent = (
    <div className="flex flex-col gap-4" style={{ width: `${CHAT_WIDTH}px` }}>
      {/* User Message */}
      <AiChatEventPayloadUserMessageComponent aiChatEvent={userMessageEvent} />

      {/* First Assistant Message (直後のassistantMessage) */}
      {firstAssistantMessage &&
        (() => {
          const payload = firstAssistantMessage.payload
          return (
            <React.Fragment key={firstAssistantMessage.id}>
              {payload.type === 'assistantMessage' && <AiChatEventPayloadAssistantMessageComponent aiChatEvent={{ ...firstAssistantMessage, payload }} />}
              {payload.type === 'error' && <AiChatEventPayloadErrorComponent aiChatEvent={{ ...firstAssistantMessage, payload }} />}
            </React.Fragment>
          )
        })()}

      {/* Tool Events (最初のassistantMessageの下に表示) */}
      {toolEvents.length > 0 &&
        (() => {
          const hasAssistantMessage = assistantMessageEvents.length >= 2
          const latestToolEvent = toolEvents[toolEvents.length - 1]!

          // assistantMessageがある場合: 「IRデータ検索完了」とAccordion
          return (
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setIsToolEventsExpanded(!isToolEventsExpanded)}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 pl-2"
              >
                {isToolEventsExpanded ? '▼' : '▶'}
                {!hasAssistantMessage
                  ? (() => {
                      const payload = latestToolEvent.payload
                      return (
                        <div className="flex flex-col gap-1 items-start">
                          {payload.type === 'serverSideToolCall' && (
                            <AiChatEventPayloadServerSideToolCallComponent aiChatEvent={{ ...latestToolEvent, payload }} />
                          )}
                          {payload.type === 'clientInfo' && <AiChatEventPayloadClientInfoComponent aiChatEvent={{ ...latestToolEvent, payload }} />}
                          {payload.type === 'clientAction' && <AiChatEventPayloadClientActionComponent aiChatEvent={{ ...latestToolEvent, payload }} />}
                        </div>
                      )
                    })()
                  : 'IRデータ検索完了'}
              </button>
              {isToolEventsExpanded && (
                <div className="flex flex-col gap-1">
                  {toolEvents.map((event) => {
                    const payload = event.payload
                    return (
                      <React.Fragment key={event.id}>
                        {payload.type === 'serverSideToolCall' && <AiChatEventPayloadServerSideToolCallComponent aiChatEvent={{ ...event, payload }} />}
                        {payload.type === 'clientInfo' && <AiChatEventPayloadClientInfoComponent aiChatEvent={{ ...event, payload }} />}
                        {payload.type === 'clientAction' && <AiChatEventPayloadClientActionComponent aiChatEvent={{ ...event, payload }} />}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}

      {/* Rest of Assistant Messages & Errors (2番目以降のassistantMessage) */}
      {restAssistantMessages.map((event) => {
        const payload = event.payload
        return (
          <React.Fragment key={event.id}>
            {payload.type === 'assistantMessage' && <AiChatEventPayloadAssistantMessageComponent aiChatEvent={{ ...event, payload }} />}
            {payload.type === 'error' && <AiChatEventPayloadErrorComponent aiChatEvent={{ ...event, payload }} />}
          </React.Fragment>
        )
      })}

      {/* Waiting Indicator */}
      {isLast &&
        (() => {
          const WaitingIndicator = () => (
            <div className="flex flex-row items-center gap-2 pl-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs text-gray-500">考えています...</span>
            </div>
          )

          if (isSendingMessage) {
            return null
          }

          // 最後のイベントを取得
          const lastEvent = [...toolEvents, ...assistantMessageEvents].sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })[0]

          if (!lastEvent) {
            return <WaitingIndicator />
          }

          if (lastEvent.payloadJsonStatus === 'generating') {
            return null
          }

          const payload = lastEvent.payload
          if (payload.type === 'assistantMessage') {
            return payload.pause ? null : <WaitingIndicator />
          } else if (payload.type === 'serverSideToolCall') {
            return payload.status === 'executing' ? null : <WaitingIndicator />
          } else if (payload.type === 'clientAction' || payload.type === 'clientInfo') {
            return <WaitingIndicator />
          }

          return null
        })()}
    </div>
  )

  return (
    <div className="flex flex-row w-dvw gap-12 overflow-x-hidden">
      <div className={cn('transition-all duration-250 ease-linear', !isAnySourceDocumentExpanded ? 'flex-1' : 'w-0')} />
      <div className={cn('transition-all duration-250 ease-linear', !isAnySourceDocumentExpanded ? 'flex-shrink-0' : 'w-1/2')}>{chatContent}</div>
      <div className={cn('transition-all duration-250 ease-linear', !isAnySourceDocumentExpanded ? 'flex-1' : 'w-1/2')}>
        <SourceDocumentPane
          queryUsecaseTableResults={queryUsecaseTableResults}
          searchSecuritiesReportChunkResults={securitiesReportChunkResults}
          showUsecaseTablesResults={showUsecaseTablesResults}
          queryRawSqlUsecaseTableResults={queryRawSqlUsecaseTableResults}
          expandedQueryUsecaseTableResults={expandedQueryUsecaseTableResults}
          expandedShowUsecaseTablesResults={expandedShowUsecaseTablesResults}
          onQueryUsecaseTableClick={handleQueryUsecaseTableClick}
          onShowUsecaseTableClick={handleShowUsecaseTableClick}
          onQueryRawSqlUsecaseTableClick={handleQueryRawSqlUsecaseTableClick}
          onCloseDetail={handleCloseDetail}
        />
      </div>
    </div>
  )
}
